import React, { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import cx from 'classnames'
import moment, { Moment } from 'moment'

import { Avatar, Button, ToolTip } from '@driftt/tide-core'

import { fetchUpcomingMeetings } from 'api'
import { driftLoadingStateBuilder } from 'components/States'
import useQueryParams from 'hooks/useQueryParams'
import { accountPath } from 'utils/paths'
import { humanizedTime } from 'utils/timeStamp'
import { segmentTrack, TrackType } from 'utils/tracking'

import { fetchCalendarStatus } from './api'
import { DEMO_MEETINGS } from './DemoData'

import styles from './styles.module.css'

const INCLUDE_DEMO_DATA_PARAM = 'includeDemoData'

export interface UpcomingMeeting {
  meetingBookedEvent: {
    eventId: string
    slotStart: number
    slotEnd: number
    scheduledAt: number
    schedulerId: number
    endUserParticipantIds?: number[]
    meetingNotes: string[]
    meetingType: string
    status: string
    meetingTitle?: string
  }
  companyIntel?: {
    name?: string
    domain?: string
    logo?: string
  }
  endUserIntel?: {
    firstName?: string
    lastName?: string
    displayName?: string
    avatarUrl?: string
    employmentTitle?: string
    linkedinHandle?: string
  }
  account?: {
    accountId: string,
    domain: string
    name?: string
  }
}

const FRIDAY_DAY_NUM = 5
const SATURDAY_DAY_NUM = 6
const SUNDAY_DAY_NUM = 7

const RELEVANT_MEETING_STATUSES = ['ACTIVE', 'RESCHEDULED']

const getMeetingEntityName = (meeting: UpcomingMeeting) => {
  return meeting.account?.name || 
    meeting.companyIntel?.name || 
    meeting.endUserIntel?.displayName ||
    'New Meeting'
}

const getMeetingDescription = (meeting: UpcomingMeeting) => {
  if (meeting.meetingBookedEvent.meetingTitle) {
    return meeting.meetingBookedEvent.meetingTitle
  }
  const participantName = meeting.endUserIntel?.displayName
  const meetingType = meeting.meetingBookedEvent.meetingType || 'New meeting'
  return participantName ? `${participantName} - ${meetingType}` : meetingType
}

const getFirstDay = () => {
  const currentDayOfWeek = moment().isoWeekday()

  if (currentDayOfWeek === SATURDAY_DAY_NUM) {
    return moment().add(2, 'day')
  } else if (currentDayOfWeek === SUNDAY_DAY_NUM) {
    return moment().add(1, 'day')
  } else {
    return moment()
  }
}

const getSecondDay = () => {
  const currentDayOfWeek = moment().isoWeekday()

  if ([FRIDAY_DAY_NUM, SATURDAY_DAY_NUM].includes(currentDayOfWeek)) {
    return moment().add(3, 'day')
  } else if (currentDayOfWeek === SUNDAY_DAY_NUM) {
    return moment().add(2, 'day')
  } else {
    return moment().add(1, 'day')
  }
}

const isToday = (dt: Moment) => moment().isSame(dt, 'day')
const isTomorrow = (dt: Moment) => moment().add(1, 'day').isSame(dt, 'day')


const CalendarDisconnectedEmptyState: React.FC = () => {
  return <div className={styles.CalendarDisconnectedEmptyState}>
    <p>Looks like your calendar is not connected!</p>
    <p>Connect your calendar to turn site visits into meetings, even if you’re offline.</p>
    <p>You’ll have full control over when and how people book time with you, and Drift will make sure you’re never double-booked.</p>
    <a target="_blank"
      rel="noopener noreferrer"
      href="https://gethelp.drift.com/hc/en-us/articles/360019448074-How-to-connect-your-calendar-to-Drift-Meetings">
        Learn more.
    </a>
    <Button onClick={() => {
      segmentTrack(TrackType.UPCOMING_MEETING_CALENDAR_CONNECT)
      window.open('/meetings/mine')
    }}>
      Connect calendar
    </Button>
  </div>
}

export const UpcomingMeetingItem: React.FC<{meeting: UpcomingMeeting}> = ({ meeting }) => {
  const meetingDate = moment(meeting.meetingBookedEvent.slotStart).format('dddd MMMM D, YYYY')
  const meetingTime = humanizedTime(meeting.meetingBookedEvent.slotStart).replace(' ', '').toLowerCase()
  const meetingEntityName = getMeetingEntityName(meeting)
  const meetingDescription = getMeetingDescription(meeting)

  const endUserData = meeting.endUserIntel || { id: meeting.meetingBookedEvent.schedulerId, type: 'END_USER' }
  const isDriftAccount = meeting.account?.accountId

  return (
    <div className={styles.UpcomingMeeting}>
      {meeting.companyIntel ?
        <Avatar className={styles.Avatar} type="company" company={meeting.companyIntel} /> :
        <Avatar className={styles.Avatar} user={endUserData} />
      }
      <div className={styles.MeetingBody}>
        {meeting.account ?
          <Link
            className={cx(styles.AccountName, styles.AccountNameLink)}
            to={accountPath(meeting.account?.accountId)}
            onClick={() => {
              segmentTrack(TrackType.UPCOMING_MEETING_ACCOUNT_LINK, {
                accountId: meeting.account?.accountId,
                meetingStart: meeting.meetingBookedEvent.slotStart,
                meetingEntityName,
                meetingDescription,
              })
            }}>
            {meeting.account.name}
          </Link>
          : <p className={styles.AccountName}>{meetingEntityName}</p>
        }
        <ToolTip flow="down" tethered render={() =>
          <>
            <div className={styles.TooltipHeader}>
            {meeting.companyIntel ?
              <Avatar className={styles.Avatar} type="company" company={meeting.companyIntel} /> :
              <Avatar className={styles.Avatar} user={endUserData} /> 
            }
              <div>
                <p>{meetingEntityName}</p>
                {meeting.companyIntel?.domain && <p className={styles.TooltipCompanyDomain}>@{meeting.companyIntel?.domain}</p>}
              </div>
            </div>
            <div className={styles.TooltipBody}>
              <p>{meetingDescription}</p>
              <p>{meetingDate}</p>
              <p>{meetingTime}</p>
              {!isDriftAccount && <p className={styles.NoAccountWarning}>No account in Drift</p>}
            </div>
          </>
        }>
          <p className={styles.MeetingTitle} onMouseOver={
            () => segmentTrack(TrackType.UPCOMING_MEETING_DESCRIPTION_HOVER, {
              meetingEntityName,
              meetingDescription,
              accountId: meeting.account?.accountId,
              meetingStart: meeting.meetingBookedEvent.slotStart
            })
          }>
            {meetingDescription}
          </p>
        </ToolTip>
      </div>
      <p className={styles.MeetingStart}>{meetingTime}</p>
    </div>
  )
}

const UpcomingMeetingsCard: React.FC = () => {

  const includeDemoData = useQueryParams().get(INCLUDE_DEMO_DATA_PARAM)

  const [loadingCalendarStatus, setLoadingCalendarStatus] = useState<boolean>(true)
  const [isCalendarConnected, setIsCalendarConnected] = useState<boolean>(true)
  const [loadingMeetings, setLoadingMeetings] = useState<boolean>(true)
  const [upcomingMeetings, setUpcomingMeetings] = useState<UpcomingMeeting[]>()

  const firstDay = useMemo(() => getFirstDay(), [])
  const secondDay = useMemo(() => getSecondDay(), [])

  const firstDayMeetings = useMemo(() => {
    if (upcomingMeetings) {
      return upcomingMeetings.filter(meeting => firstDay.isSame(moment(meeting.meetingBookedEvent.slotStart), 'day'))
    }
  }, [upcomingMeetings, firstDay])
  const secondDayMeetings = useMemo(() => {
    if (upcomingMeetings) {
      return upcomingMeetings.filter(meeting => secondDay.isSame(moment(meeting.meetingBookedEvent.slotStart), 'day'))
    }
  }, [upcomingMeetings, secondDay])


  useEffect(() => {
    fetchUpcomingMeetings(secondDay.endOf('day').unix() * 1000)
      .then(data => {
        const upcoming: UpcomingMeeting[] = includeDemoData ? [...DEMO_MEETINGS, ...data] : data
        const filtered = upcoming.filter(meeting => RELEVANT_MEETING_STATUSES.includes(meeting.meetingBookedEvent.status))
        setUpcomingMeetings(filtered.sort((a, b) => a.meetingBookedEvent.slotStart - b.meetingBookedEvent.slotStart))
        if (filtered.length > 0) {
          segmentTrack(TrackType.UPCOMING_MEETING_SAW_MEETINGS, {
            numMeetings: filtered.length
          })
        } else {
          segmentTrack(TrackType.UPCOMING_MEETING_SAW_EMPTY_STATE)
        }
      })
      .catch(e => console.error(e))
      .finally(() => setLoadingMeetings(false))
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchCalendarStatus()
      .then(data => {
        const calendarConnected = data.googleAuthed || data.microsoftAuthed || data.connected
        setIsCalendarConnected(calendarConnected)
        if (!calendarConnected) {
          segmentTrack(TrackType.UPCOMING_MEETING_SAW_CALENDAR_DISCONNECTED_STATE)
        }
      })
      .catch(e => console.error(e))
      .finally(() => setLoadingCalendarStatus(false))
  }, [])

  if (loadingMeetings || loadingCalendarStatus) {
    return driftLoadingStateBuilder('Loading your meetings...')()
  }

  return (
    <div className={styles.UpcomingMeetingsCard}>
      <h4>UPCOMING MEETINGS</h4>
      {!isCalendarConnected ?
        <CalendarDisconnectedEmptyState /> :
        (firstDayMeetings?.length === 0 && secondDayMeetings?.length === 0) ?
          <div className={styles.NoMeetingsEmptyState}>
            <p>You have no upcoming meetings in the next 48 hours</p>
            <p>Only non-recurring, external meetings booked through Drift will show here.</p>
          </div>
        : <div className={styles.MeetingsContainer}>
            {firstDayMeetings && firstDayMeetings.length > 0 &&
              <>
                <p className={styles.SectionLabel}>{isToday(firstDay) ? 'Today' : firstDay.format('dddd')}</p>
                {firstDayMeetings.map((meeting, i) => <UpcomingMeetingItem key={'today-meeting-' + i} meeting={meeting} />)}
              </>
            }
            {secondDayMeetings && secondDayMeetings.length > 0 && 
              <>
                <p className={styles.SectionLabel}>{isTomorrow(secondDay) ? 'Tomorrow' : secondDay.format('dddd')}</p>
                {secondDayMeetings.map((meeting, i) => <UpcomingMeetingItem key={'tomorrow-meeting-' + i} meeting={meeting} />)}
              </>
            }
          </div>
      }
    </div>
  )
}

export default UpcomingMeetingsCard
